import { BoxProps } from '@chakra-ui/react';
import { IRevenueRecognitionResponse } from '@monetize/types/app';
import { formatCurrency } from '@monetize/utils/core';
import {
  AppCellContext,
  AppColumnDef,
  createColumnHelper,
} from '../../components/Monetize/DataTableNew/appTable';
import { format as formatDate } from 'date-fns/format';
import { parse as parseDate } from 'date-fns/parse';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import { useMemo } from 'react';
import { MBox } from '../../components/Monetize';
import { DataTable } from '../../components/Monetize/DataTableNew/DataTable';
import './RevRecTable.scss';
import {
  getRevRecPeriods,
  transformRevRecToTabularData,
} from './rev-rec.utils';

interface RevRecTableProps extends BoxProps {
  revRecData: IRevenueRecognitionResponse;
}

type RowData = Record<string, string | number | null>;

const columnHelper = createColumnHelper<RowData>();

const COLUMN_WIDTH = 140;

/**
 * Format a period string (yyyy-MM) to display format (MMM yyyy)
 */
const formatPeriod = (period: string) =>
  formatDate(parseDate(period, 'yyyy-MM', new Date()), 'MMM yyyy');

/**
 * Cell renderer for the "Month" / period column in the new table
 */
const monthCellTemplate = (info: AppCellContext<RowData, unknown>) => {
  const row = info.row.original;
  if (row.type === 'TOTAL') {
    return (
      <MBox fontWeight={600} px="4" py="2">
        {row.period}
      </MBox>
    );
  }
  if (!isString(row.period)) {
    return '';
  }
  return (
    <MBox px="4" py="2">
      {formatPeriod(row.period)}
    </MBox>
  );
};

/**
 * Creates a cell renderer for amount columns in the new table.
 * For null values, renders a shaded placeholder cell.
 * For numeric values, renders formatted currency with 0 decimal places.
 */
const createAmountCellTemplate = (field: string) => {
  return (info: AppCellContext<RowData, unknown>) => {
    const row = info.row.original;
    const value = row[field];
    if (!isNumber(value)) {
      return <MBox bg="tBlue.hover" w="100%" h="100%" />;
    }
    return (
      <MBox px="4" py="2" w="100%" h="100%">
        {formatCurrency(value as unknown as string, {
          currency: isString(row?.currency) ? row.currency : 'USD',
          maximumFractionDigits: 0,
        })}
      </MBox>
    );
  };
};

/**
 * Shared cell meta styles for TOTAL row highlighting.
 * DataTableNew applies `meta.cellStyles` to each cell's <Td>.
 * Since TOTAL row styling is row-level, we handle it via CSS
 * on the wrapper instead.
 */

/**
 * Build TanStack column definitions with two-level header groups.
 *
 * Group structure:
 *   "Sold"                  -> Month, Total          (pinned left)
 *   "Recognition Schedule"  -> dynamic period cols   (scrollable)
 *   "As Of MMM yyyy"        -> Recognized, Remaining (pinned right)
 */
function getColumns(
  periods: string[],
  recognizedAsOfPeriod: string,
): AppColumnDef<RowData, any>[] {
  const asOfLabel = `As Of ${formatPeriod(recognizedAsOfPeriod)}`;

  return [
    // Group: Sold (pinned left)
    columnHelper.group({
      id: 'sold',
      header: 'Sold',
      columns: columnHelper.columns([
        columnHelper.accessor('period', {
          header: 'Month',
          size: COLUMN_WIDTH,
          minSize: COLUMN_WIDTH,
          cell: monthCellTemplate,
          enableSorting: false,
        }),
        columnHelper.accessor('total', {
          header: 'Total',
          size: COLUMN_WIDTH,
          minSize: COLUMN_WIDTH,
          cell: createAmountCellTemplate('total'),
          enableSorting: false,
        }),
      ]),
    }),
    // Group: Recognition Schedule (dynamic period columns)
    columnHelper.group({
      id: 'recognitionSchedule',
      header: 'Recognition Schedule',
      // Built dynamically from the period list, so `columnHelper.columns()`
      // (which needs a literal tuple to preserve each TValue) does not apply.
      // v9 types a group's nested `columns` as `ColumnDef<F, TData, unknown>`,
      // so the per-column TValue is erased here regardless.
      columns: periods.map((period) =>
        columnHelper.accessor(period, {
          header: formatPeriod(period),
          size: COLUMN_WIDTH,
          minSize: COLUMN_WIDTH,
          cell: createAmountCellTemplate(period),
          enableSorting: false,
        }),
      ) as AppColumnDef<RowData, unknown>[],
    }),
    // Group: As Of (pinned right)
    columnHelper.group({
      id: 'asOf',
      header: asOfLabel,
      columns: columnHelper.columns([
        columnHelper.accessor('recognized', {
          header: 'Recognized',
          size: COLUMN_WIDTH,
          minSize: COLUMN_WIDTH,
          cell: createAmountCellTemplate('recognized'),
          enableSorting: false,
        }),
        columnHelper.accessor('deferred', {
          header: 'Remaining',
          size: COLUMN_WIDTH,
          minSize: COLUMN_WIDTH,
          cell: createAmountCellTemplate('deferred'),
          enableSorting: false,
        }),
      ]),
    }),
  ];
}

export const RevRecTable = ({ revRecData, ...rest }: RevRecTableProps) => {
  // Derive periods and rows for DataTableNew
  const { rows, columns } = useMemo(() => {
    const { recognizedAsOfPeriod, startPeriod } = revRecData;
    const periods = getRevRecPeriods(startPeriod, recognizedAsOfPeriod);
    const results = transformRevRecToTabularData(revRecData);
    const allRows = results.rows.concat(results.footer).map((row, index) => ({
      ...row,
      _rowId: String(index),
    }));
    return {
      rows: allRows,
      columns: getColumns(periods, recognizedAsOfPeriod),
    };
  }, [revRecData]);

  return (
    <MBox
      className="revenue-table"
      {...rest}
      css={{
        '& tr:last-of-type td': {
          // The TOTAL row is always the last row
        },
        ...rest.css,
      }}
    >
      <DataTable
        columns={columns}
        data={rows}
        enablePagination={false}
        clientControlled
        skipFilterValuePreprocessing
        pinnedLeftColumns={['period', 'total']}
        pinnedRightColumns={['recognized', 'deferred']}
        tableProps={{
          bodyRow: {
            cursor: 'default',
            _hover: {
              backgroundColor: 'inherit',
            },
          },
        }}
        containerProps={{
          maxHeight: 'none',
          pb: 0,
        }}
        getRowId={(row) => (row as any)._rowId}
      />
    </MBox>
  );
};
